import { useState } from 'react'
import { forwardRef } from 'react'
import { useRef } from 'react'
import { useEffect } from 'react'
import { Box } from '@mui/material'
import { Grid } from '@mui/material'
import { Paper } from '@mui/material'
import { Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { CompetitorsIcon } from 'shared/icons/CompetitorsIcon';
import { MaxPriceIcon } from 'shared/icons/MaxPriceIcon';
import { MinPriceIcon } from 'shared/icons/MinPriceIcon';
import { AveragePriceIcon } from 'shared/icons/AveragePriceIcon';
import { DateTime } from 'luxon'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useReadHostServicesByMonitorRunIdQuery } from 'services/hostService'
import { SortOrder } from 'shared/enums'
import { ReadHostServicesByMonitorRunIdParams } from 'services/hostService/qhostService'
import FoundationIcon from '@mui/icons-material/Foundation'
import { ReadMonitorRunResultParams, useReadMonitorRunResultGroupedByMonitorIdQuery } from 'services/monitorRunResult'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import UpdateIcon from '@mui/icons-material/Update'
import { useReadMonitorByIdQuery } from 'services/monitor'
import { useUpdateMonitorOwnPriceMutation } from 'services/monitor'

const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#EEEEEE',
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: 'center',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: theme.palette.text.primary,
}));

interface SummarySectionProps {
    marketSpyRunResult: any,
    onTitleClick?: Function,
    marketSpyId?: number
}

export const SummarySection = forwardRef((props: SummarySectionProps, ref: any): JSX.Element => {
    const {
        marketSpyRunResult,
        onTitleClick,
        marketSpyId
    } = props;

    const [readHostServicesByMonitorRunIdParams, setReadHostServicesByMonitorRunIdParams] = useState<ReadHostServicesByMonitorRunIdParams>({
        monitorRunId: marketSpyRunResult?.id || -1,
        sorting: {
            orderField: '',
            sortOrder: SortOrder.Ascending
        }
    })
    const [skip, setSkip] = useState<boolean>(marketSpyRunResult === undefined);
    const [order, setOrder] = useState<SortOrder>(SortOrder.Ascending);
    const [expandWasOpened, setExpandWasOpened] = useState<Boolean>(false);
    const summaryRootComponent = useRef<null | HTMLDivElement>(null); 
    const [updateMonitorOwnPrice] = useUpdateMonitorOwnPriceMutation();
    const useReadHostServicesByMonitorRunIdQueryOptions = {
        skip: skip
    }

    const {
        data: readHostServicesByMonitorRunIdData,
        isLoading: readHostServicesByMonitorRunIdIsLoading,
        isFetching: readHostServicesByMonitorRunIdisFetching
    } = useReadHostServicesByMonitorRunIdQuery(
        readHostServicesByMonitorRunIdParams,
        useReadHostServicesByMonitorRunIdQueryOptions
        );

    useEffect(() => {
        setSkip(marketSpyRunResult === undefined);
        if (marketSpyRunResult !== undefined) {
            setReadHostServicesByMonitorRunIdParams(params => ({
                ...params,
                monitorRunId: marketSpyRunResult?.id
            }));
        }
        
    }, [marketSpyRunResult]);

    const [skipReadMonitorRunResultGroupedByMonitorId, setSkipReadMonitorRunResultGroupedByMonitorId] = useState<boolean>(true);
    const readMonitorRunResultByMonitorIdQueryParams: ReadMonitorRunResultParams = {
        monitorId: marketSpyId,
    }; 

    useEffect(() => {
        setSkipReadMonitorRunResultGroupedByMonitorId(marketSpyId === undefined || marketSpyId === -1);
    }, [marketSpyId]);

    const readMonitorRunResultByMonitorIdQueryOptions = {
        skip: skipReadMonitorRunResultGroupedByMonitorId,
        refetchOnMountOrArgChange: true,
        pollingInterval: 5000,
    };

    const {
        data: readMonitorRunResultGroupedByMonitorIdData,
        isLoading: readMonitorRunResultGroupedByMonitorIdsLoading,
        isFetching: readMonitorRunResultGroupedByMonitorIdIsFetching,
    } = useReadMonitorRunResultGroupedByMonitorIdQuery(
        readMonitorRunResultByMonitorIdQueryParams,
        readMonitorRunResultByMonitorIdQueryOptions
        );

    const checkinUtc = DateTime.fromISO(marketSpyRunResult?.checkinUtc).toFormat('M/dd/yyyy');
    const checkOutUtc = DateTime.fromISO(marketSpyRunResult?.checkOutUtc).toFormat('M/dd/yyyy');  

    const renderAccordionSummaryHeading = () => {
        if (marketSpyRunResult !== undefined && marketSpyRunResult?.checkinUtc !== undefined) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <Typography sx={
                        {
                            fontSize: {
                                xs: '24px',
                                sm: '24px',
                                md: '24px',
                            },
                            lineHeight: '28px',
                            color: '#000',
                            fontWeight: 600,
                            marginRight: 2
                        }
                    }
                    >Daily Summary
                    </Typography>
                    <Box sx={{
                        fontSize: {
                            xs: '16px',
                            sm: '18px',
                            md: '24px',
                        },
                        lineHeight: '28px',
                        color: '#000',
                        marginRight: 2,
                        display: 'flex', 
                        gap: '6px'
                    }}
                        onClick={ handleTitleCLick }
                    >
                        <Box>{`${checkinUtc}`}</Box>
                        <CalendarMonthIcon></CalendarMonthIcon>
                    </Box>
                </Box>
            )
        }

        return (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <Typography sx={
                        {
                            fontSize: {
                                xs: '24px',
                                sm: '24px',
                                md: '24px',
                            },
                            lineHeight: '28px',
                            color: '#000',
                            fontWeight: 600,
                            marginRight: 2
                        }
                    }
                >Daily Summary
                </Typography>
                <Typography sx={{
                    fontSize: {
                        xs: '16px',
                        sm: '18px',
                        md: '24px',
                    },
                    lineHeight: '28px',
                    color: '#c62828',
                    marginRight: 2
                }}
                >{`Select period above`}
                </Typography>
            </Box>
        )
    }

    const [readMonitorByIdQueryParams, setReadMonitorByIdQueryParams] = useState<any>(undefined);
    const [readMonitorByIdOptions, setReadMonitorByIdOptions] = useState<any>(
        {
            skip: true
        }
    );


    const {
        data: readMonitorByIdQueryData,
        isLoading: readMonitorByIdQueryIsLoading
    } = useReadMonitorByIdQuery(readMonitorByIdQueryParams, readMonitorByIdOptions);

    useEffect(() => {
        setReadMonitorByIdQueryParams({
            ...readMonitorByIdQueryParams,
            monitorId: marketSpyId
        });

        setReadMonitorByIdOptions(
            {
                ...readMonitorByIdOptions,
                skip: marketSpyId === undefined,
            }
        );
    }, [
        marketSpyId
    ]);

    const handleTitleCLick = (params: any) => {
        if (typeof onTitleClick === "function") {
            onTitleClick(params);
        }
    }

    const [price, setPrice] = useState<number>(0);
    useEffect(() => {

        if (marketSpyRunResult?.ownPrice === undefined) {
            if (readMonitorByIdQueryData === undefined) {
                setPrice(0);
                return;
            }

            setPrice(readMonitorByIdQueryData.priceForComparison);
            return;
        }

        if (marketSpyRunResult?.ownPrice !== undefined && marketSpyRunResult?.ownPrice === null) {
            if (readMonitorByIdQueryData === undefined) {
                setPrice(0);
                return;
            }

            setPrice(readMonitorByIdQueryData.priceForComparison);
            return;
        }

        setPrice(marketSpyRunResult?.ownPrice);
    }, [marketSpyRunResult, readMonitorByIdQueryData]);

    const getNumberValue = (value: any) => {
        if (value === undefined || value === null) {
            return 'N/A';
        }

        return `${value}`; 
    }

    const getMoneyValue = (value: any) => {
        if (value === undefined || value === null) {
            return 'N/A';
        }

        if (value === 0) {
            return 'N/A';
        }

        return `$${Math.round(value)}`; 
    }

    const getDateValue = (value: any) => {
        if (value === undefined || value === null) {
            return 'N/A';
        }

        return `${DateTime.fromISO(marketSpyRunResult?.timestampUtc).toFormat('M/dd/yyyy hh:mm  a') }`;
    }

    const namePropStyle = {
        fontWeight: '700',
        fontSize: '20px'
    }

    const valuePropStyle = {
        margin: '0 0 10px 10px',
        fontSize: '20px'
    }

    const propertyListContainer = {
        padding: '20px 24px',
        gridTemplateColumns: 'max-content 1fr',
        display: 'grid',

    }

    return (
        <>
            <Accordion
                ref={ref}
                disabled={marketSpyRunResult === undefined}
                expanded={marketSpyRunResult !== undefined}
                disableGutters
                defaultExpanded={false}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: '#FFF' }} />}
                    aria-controls="panel1a-content"
                    sx={{
                        backgroundColor: '#FFF',
                        flexDirection: 'row-reverse',
                        padding: {
                            xs: '8px 12px', md: '8px 24px'
                        },
                        '& .MuiAccordionSummary-expandIconWrapper': {
                            padding: '12px 12px 12px 12px',
                            fill: '#717171',
                            color: '#717171'
                        },

                        '& .MuiSvgIcon-root': {
                            fill: '#717171',
                            color: '#717171'
                        },

                        '& .MuiAccordionSummary-content': {
                            flexDirection: {
                                xs: 'column',
                                sm: 'row'
                            }
                        },

                    }}
                >
                    {renderAccordionSummaryHeading() }
                </AccordionSummary>
                <AccordionDetails sx={{ bgcolor: '#EEE', p: 2 }}>
                    <Box sx={propertyListContainer}>
                        <Box sx={namePropStyle}>
                            Date:
                        </Box>
                        <Box sx={valuePropStyle}>{`${checkinUtc}`}</Box>
                        <Box sx={namePropStyle}>
                            Competitors:
                        </Box>
                        <Box sx={valuePropStyle}>
                            {
                                getNumberValue(marketSpyRunResult?.competitors)
                            }
                        </Box>
                        <Box sx={namePropStyle}>
                            Median price:
                        </Box>
                        <Box sx={valuePropStyle}>
                            {
                                getMoneyValue(marketSpyRunResult?.medianPrice)
                            }
                        </Box>
                        <Box sx={namePropStyle}>
                            Min price:
                        </Box>
                        <Box sx={valuePropStyle}>
                            {
                                getMoneyValue(marketSpyRunResult?.minPrice)
                            }
                        </Box>
                        <Box sx={namePropStyle}>
                            Max price:
                        </Box>
                        <Box sx={valuePropStyle}>
                            {
                                getMoneyValue(marketSpyRunResult?.maxPrice)
                            }
                        </Box>
                        <Box sx={namePropStyle}>
                            Supply:
                        </Box>
                        <Box sx={valuePropStyle}>
                            {
                                getNumberValue(marketSpyRunResult?.supply)
                            }
                        </Box>
                        <Box sx={namePropStyle}>
                            Demand:
                        </Box>
                        <Box sx={valuePropStyle}>
                            {
                                getNumberValue(marketSpyRunResult?.demand)
                            }
                        </Box>

                        <Box sx={namePropStyle}>
                            Updated:
                        </Box>
                        <Box sx={valuePropStyle}>
                            {
                                getDateValue(marketSpyRunResult?.timestampUtc)
                            }
                        </Box>

                    </Box>
                </AccordionDetails>
            </Accordion>
        </>
    )
})